/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { CosmosEnumTimeAndDateFormat, CosmosEnumTimeZone } from "@cosmos/hygraph-schema";

// Time unit thresholds (in seconds) for relative time formatting
const TIME_UNITS: ReadonlyArray<{ unit: Intl.RelativeTimeFormatUnit; seconds: number }> = [
	{ unit: "year", seconds: 31536000 },
	{ unit: "month", seconds: 2592000 },
	{ unit: "week", seconds: 604800 },
	{ unit: "day", seconds: 86400 },
	{ unit: "hour", seconds: 3600 },
	{ unit: "minute", seconds: 60 },
	{ unit: "second", seconds: 1 },
] as const;

// Cache for Intl.RelativeTimeFormat instances by locale
const rtfCache = new Map<string, Intl.RelativeTimeFormat>();

/**
 * Gets or creates a cached Intl.RelativeTimeFormat instance for the given locale
 */
const getRelativeTimeFormatter = (locale: string): Intl.RelativeTimeFormat => {
	if (!rtfCache.has(locale)) {
		rtfCache.set(locale, new Intl.RelativeTimeFormat(locale, { numeric: "auto" }));
	}
	return rtfCache.get(locale)!;
};

/**
 * Finds the maximum appropriate unit for a time difference and formats it
 * @param diffInSeconds - The time difference in seconds
 * @param rtf - The RelativeTimeFormat instance to use for formatting
 * @returns A formatted relative time string, or empty string if no appropriate unit found
 */
const getMaxUnit = (diffInSeconds: number, rtf: Intl.RelativeTimeFormat): string => {
	for (const { unit, seconds } of TIME_UNITS) {
		const value = Math.round(diffInSeconds / seconds);
		if (Math.abs(diffInSeconds) >= seconds || unit === "second") {
			return rtf.format(value, unit);
		}
	}
	return "";
};

/**
 * Converts a date to a relative time string (e.g., "5 months ago", "2 years ago", "3 days ago")
 * Uses Intl.RelativeTimeFormat for i18n support with explicit en-US fallback
 * @param date - The date string to format
 * @param locale - The locale to use for formatting (e.g., "en-US", "ja", "de")
 * @returns A formatted relative time string
 */
export const dateToTimeAgo = (date: string, locale: string = "en-US"): string => {
	const now = Date.now();
	const then = new Date(date).getTime();
	const diffInSeconds = Math.round((then - now) / 1000);

	try {
		// Ensure we have a valid locale, fall back to en-US if not
		const safeLocale = locale?.trim() || "en-US";

		// Get cached formatter
		const rtf = getRelativeTimeFormatter(safeLocale);

		// Find the appropriate unit
		return getMaxUnit(diffInSeconds, rtf);
	} catch (e) {
		console.error(e);
		// If formatting fails, try with en-US as last resort
		try {
			const rtf = getRelativeTimeFormatter("en-US");
			return getMaxUnit(diffInSeconds, rtf);
		} catch {}
	}
	return "";
};

export const enumTimeZoneToIANA = (enumValue?: string | null): string => {
	if (!enumValue) return "";
	if (enumValue.startsWith("Etc_GMT_plus")) {
		return enumValue.replace("Etc_GMT_plus_", "Etc/GMT+");
	}
	// replace only the first underscore with a slash
	const [region, ...rest] = enumValue.split("_");
	return `${region}/${rest.join("_")}`;
};

export const formatTimeAndDate = (
	timeAndDate: string,
	locale = "en-US",
	timeAndDateFormat: CosmosEnumTimeAndDateFormat | null = CosmosEnumTimeAndDateFormat.MonthDayYear,
	timeZone?: CosmosEnumTimeZone | null,
): string => {
	// split date to avoid timezone adjustments by Date constructor
	const [dayPart, timePart] = timeAndDate.split("T");
	const [y, m, d] = dayPart.split("-").map((i) => parseInt(i, 10));
	const [hh, mm] = timePart ? [parseInt(timePart.slice(0, 2), 10), parseInt(timePart.slice(3, 5), 10)] : [0, 0];

	let dateObj: Date;
	if (
		timeAndDateFormat === CosmosEnumTimeAndDateFormat.WeekdayMonthDayTimeTimeZone ||
		timeAndDateFormat === CosmosEnumTimeAndDateFormat.WeekdayMonthDayYearTimeTimeZone
	) {
		dateObj = new Date(Date.UTC(y, m - 1, d, hh, mm)); // month is 0-indexed
	} else {
		dateObj = new Date(y, m - 1, d, hh, mm); // month is 0-indexed
	}

	if (isNaN(dateObj.getTime())) throw new Error("Invalid date"); // ensure successful conversion

	switch (timeAndDateFormat) {
		case CosmosEnumTimeAndDateFormat.WeekdayMonthDayTimeTimeZone:
			return dateObj.toLocaleDateString(locale, {
				weekday: "long",
				month: "short",
				day: "numeric",
				hour: "numeric",
				minute: "numeric",
				timeZone: enumTimeZoneToIANA(timeZone) || undefined,
				timeZoneName: "short",
			});
		case CosmosEnumTimeAndDateFormat.WeekdayMonthDayYearTimeTimeZone:
			return dateObj.toLocaleDateString(locale, {
				weekday: "long",
				month: "short",
				day: "numeric",
				year: "numeric",
				hour: "numeric",
				minute: "numeric",
				timeZone: enumTimeZoneToIANA(timeZone) || undefined,
				timeZoneName: "short",
			});
		case CosmosEnumTimeAndDateFormat.MonthDay:
			return dateObj.toLocaleDateString(locale, { month: "long", day: "numeric" });
		case CosmosEnumTimeAndDateFormat.MonthYear:
			return dateObj.toLocaleDateString(locale, { month: "long", year: "numeric" });
		default:
			return dateObj.toLocaleDateString(locale, { month: "long", day: "numeric", year: "numeric" });
	}
};

export const hygraphDateToLocaleDateString = (
	hygraphDate?: string | null,
	hygraphEndDate?: string | null,
	locale?: string,
	timeAndDateFormat?: CosmosEnumTimeAndDateFormat | null,
	timeZone?: CosmosEnumTimeZone | null,
): string => {
	if (!hygraphDate) return "";
	const formattedStart = formatTimeAndDate(hygraphDate, locale, timeAndDateFormat, timeZone);
	if (hygraphEndDate) {
		const formattedEnd = formatTimeAndDate(hygraphEndDate, locale, timeAndDateFormat, timeZone);
		return `${formattedStart} - ${formattedEnd}`;
	}
	return formattedStart;
};

/**
 * Safely converts a time/date value to a timestamp number.
 * Handles string, number, Date object, and invalid/null/undefined values.
 * @param timeAndDate - The time/date value to convert (string, number, Date, or unknown)
 * @returns The timestamp in milliseconds, or 0 if the value cannot be converted
 */
export const safeGetTime = (timeAndDate: unknown): number => {
	if (!timeAndDate) return 0;
	if (typeof timeAndDate === "string" || typeof timeAndDate === "number") {
		return new Date(timeAndDate).getTime();
	}
	if (timeAndDate instanceof Date) {
		return timeAndDate.getTime();
	}
	return 0;
};
